/**
 * Email triage inbox (EDG-8483, plan item 9) — the one new standalone surface.
 *
 * Lists the org's untriaged emails (orphans + suggested-only) and reference-only
 * quarantine notices from
 * GET /api/crm/email/triage and lets a broker resolve them in bulk:
 *   - Include  — confirm the selected emails' suggested links (PATCH included=true)
 *   - Exclude  — dismiss the selected emails' suggested links (PATCH included=false)
 *   - Link to… — attach the selected emails to a chosen entity (POST a new link)
 *
 * Bulk actions fan out over the existing per-link / per-email link endpoints; there
 * is no bulk mutation endpoint. Styling holds to the design tokens.
 */

import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Inbox, Link2, Loader2, RefreshCw } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { EmailThreadItem } from "@/components/email/email-thread-item";
import { InclusionControl } from "@/components/email/inclusion-control";

type EntityType = "contact" | "account" | "deal" | "client_group";

interface TriageSuggestedLink {
  id: string;
  entityType: EntityType;
  entityId: string;
  linkSource: string;
  confidence: number | null;
  provenance: Record<string, unknown>;
}

interface TriageItem {
  id: string;
  itemType: "email" | "email_quarantine";
  threadId: string | null;
  occurredAt: string;
  subject: string | null;
  preview: string | null;
  direction: "inbound" | "outbound" | null;
  messageCount: number;
  hasAttachments: boolean;
  latestMessageId: string | null;
  outlookWebLink: string | null;
  from: unknown;
  suggestedLinks: TriageSuggestedLink[];
  externalRef: string | null;
  quarantineReasonCode: string | null;
}

interface TriageResponse {
  items: TriageItem[];
  nextCursor: { cursorTs: string; cursorId: string } | null;
}

const TRIAGE_KEY = "/api/crm/email/triage";

function isActionableEmail(
  item: TriageItem | undefined,
): item is TriageItem & { itemType: "email"; latestMessageId: string } {
  return item?.itemType === "email" && item.latestMessageId !== null;
}

export default function EmailTriageInboxPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const triageQuery = useInfiniteQuery({
    queryKey: [TRIAGE_KEY],
    initialPageParam: null as TriageResponse["nextCursor"] | null,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageParam?.cursorTs) params.set("cursorTs", pageParam.cursorTs);
      if (pageParam?.cursorId) params.set("cursorId", pageParam.cursorId);
      const res = await apiRequest("GET", `${TRIAGE_KEY}?${params.toString()}`);
      return (await res.json()) as TriageResponse;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
  });

  const items = useMemo(
    () => triageQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [triageQuery.data?.pages],
  );

  const itemsById = useMemo(() => {
    const map = new Map<string, TriageItem>();
    for (const item of items) map.set(item.id, item);
    return map;
  }, [items]);

  const selectableItems = useMemo(
    () => items.filter((item) => isActionableEmail(item)),
    [items],
  );

  const selectedItems = useMemo(
    () => Array.from(selected).map((id) => itemsById.get(id)).filter(isActionableEmail),
    [selected, itemsById],
  );

  const selectedSuggestedLinkIds = useMemo(
    () => selectedItems.flatMap((item) => item.suggestedLinks.map((link) => link.id)),
    [selectedItems],
  );

  const allSelected = selectableItems.length > 0 && selected.size === selectableItems.length;

  const toggleOne = (id: string, on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAll = (on: boolean) => {
    setSelected(on ? new Set(selectableItems.map((item) => item.id)) : new Set());
  };

  const afterMutation = (title: string) => {
    setSelected(new Set());
    setLinkDialogOpen(false);
    queryClient.invalidateQueries({ queryKey: [TRIAGE_KEY] });
    toast({ title });
  };

  const setInclusionMutation = useMutation({
    mutationFn: async (included: boolean) => {
      await Promise.all(
        selectedSuggestedLinkIds.map((linkId) =>
          apiRequest("PATCH", `/api/crm/email/links/${linkId}`, { included }),
        ),
      );
      return included;
    },
    onSuccess: (included) =>
      afterMutation(included ? "Emails included" : "Emails excluded"),
    onError: (error: Error) =>
      toast({ title: "Bulk update failed", description: error.message, variant: "destructive" }),
  });

  const linkMutation = useMutation({
    mutationFn: async (target: { entityType: EntityType; entityId: string }) => {
      await Promise.all(
        selectedItems.map((item) =>
          apiRequest("POST", `/api/crm/email/${item.latestMessageId}/links`, {
            entityType: target.entityType,
            entityId: target.entityId,
            included: true,
            linkSource: "manual",
          }),
        ),
      );
    },
    onSuccess: () => afterMutation("Emails linked"),
    onError: (error: Error) =>
      toast({ title: "Linking failed", description: error.message, variant: "destructive" }),
  });

  const isMutating = setInclusionMutation.isPending || linkMutation.isPending;
  const hasSuggestions = selectedSuggestedLinkIds.length > 0;

  return (
    <div className="flex h-full flex-col" data-testid="page-email-triage">
      <div className="flex items-center justify-between border-b bg-card p-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-xl font-semibold">Email triage</h1>
          {items.length > 0 ? (
            <Badge variant="secondary" data-testid="triage-count">
              {items.length}
            </Badge>
          ) : null}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => triageQuery.refetch()}
          data-testid="button-refresh-triage"
        >
          <RefreshCw className="mr-1 h-3 w-3" />
          Refresh
        </Button>
      </div>

      {selected.size > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2"
          data-testid="triage-bulk-bar"
        >
          <span className="text-sm font-medium" data-testid="triage-selected-count">
            {selected.size} selected
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              disabled={!hasSuggestions || isMutating}
              onClick={() => setInclusionMutation.mutate(true)}
              data-testid="button-bulk-include"
            >
              {isMutating ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Include
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!hasSuggestions || isMutating}
              onClick={() => setInclusionMutation.mutate(false)}
              data-testid="button-bulk-exclude"
            >
              Exclude
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isMutating}
              onClick={() => setLinkDialogOpen(true)}
              data-testid="button-bulk-link"
            >
              <Link2 className="mr-1 h-3 w-3" />
              Link to…
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto p-3">
        {triageQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : triageQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm">
            <p className="font-medium">Triage inbox failed to load</p>
            <p className="mt-1 text-muted-foreground">{(triageQuery.error as Error).message}</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => triageQuery.refetch()}>
              <RefreshCw className="mr-1 h-3 w-3" />
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed p-8 text-center" data-testid="triage-empty">
            <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">Inbox zero</p>
            <p className="mt-1 text-xs text-muted-foreground">
              No emails are waiting to be triaged.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {selectableItems.length > 0 ? (
              <label className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={(value) => toggleAll(value === true)}
                  data-testid="triage-select-all"
                />
                Select all
              </label>
            ) : null}

            {items.map((item) => {
              if (item.itemType === "email_quarantine") {
                return (
                  <div
                    key={item.id}
                    className="rounded-md border bg-card p-3"
                    data-testid={`triage-quarantine-${item.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="destructive">Quarantined</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.occurredAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-medium">{item.subject}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{item.preview}</p>
                    <p className="mt-1 break-all text-xs text-muted-foreground">{item.externalRef}</p>
                  </div>
                );
              }

              const isSelected = selected.has(item.id);
              return (
                <EmailThreadItem
                  key={item.id}
                  testId="triage-email-item"
                  className={isSelected ? "ring-1 ring-primary" : undefined}
                  item={item}
                  actions={
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={(value) => toggleOne(item.id, value === true)}
                      aria-label="Select email"
                      data-testid={`triage-select-${item.id}`}
                    />
                  }
                  footer={
                    item.suggestedLinks.length > 0 ? (
                      <div className="space-y-1.5">
                        {item.suggestedLinks.map((link) => (
                          <div key={link.id} className="flex items-center justify-between gap-2">
                            <span className="text-xs text-muted-foreground">
                              Suggested: <span className="capitalize text-foreground">{link.entityType.replace("_", " ")}</span>
                            </span>
                            <InclusionControl
                              testId={`triage-inclusion-${link.id}`}
                              value={null}
                              linkSource={link.linkSource}
                              confidence={link.confidence}
                              provenance={link.provenance}
                              onChange={(next) => {
                                if (next === null) return;
                                apiRequest("PATCH", `/api/crm/email/links/${link.id}`, {
                                  included: next,
                                })
                                  .then(() => {
                                    queryClient.invalidateQueries({ queryKey: [TRIAGE_KEY] });
                                    toast({ title: next ? "Email included" : "Email excluded" });
                                  })
                                  .catch((error: Error) =>
                                    toast({
                                      title: "Update failed",
                                      description: error.message,
                                      variant: "destructive",
                                    }),
                                  );
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No suggestion — select and use “Link to…” to file this email.
                      </p>
                    )
                  }
                />
              );
            })}

            {triageQuery.hasNextPage ? (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={triageQuery.isFetchingNextPage}
                  onClick={() => triageQuery.fetchNextPage()}
                  data-testid="button-load-more-triage"
                >
                  {triageQuery.isFetchingNextPage ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : null}
                  Load more
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <EntityLinkDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        pending={linkMutation.isPending}
        onPick={(entityType, entityId) => linkMutation.mutate({ entityType, entityId })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

interface EntityOption {
  entityType: EntityType;
  id: string;
  label: string;
}

function EntityLinkDialog({
  open,
  onOpenChange,
  pending,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onPick: (entityType: EntityType, entityId: string) => void;
}) {
  const [search, setSearch] = useState("");

  const contacts = useQuery<any[]>({ queryKey: ["/api/crm/contacts"], enabled: open });
  const accounts = useQuery<any[]>({ queryKey: ["/api/crm/accounts"], enabled: open });
  const deals = useQuery<any[]>({ queryKey: ["/api/crm/deals"], enabled: open });
  const groups = useQuery<any[]>({ queryKey: ["/api/crm/client-groups"], enabled: open });

  const term = search.trim().toLowerCase();

  const options = useMemo<EntityOption[]>(() => {
    const named = (label: string) => label.toLowerCase().includes(term);
    const out: EntityOption[] = [];
    for (const c of contacts.data ?? []) {
      const label = [c.firstName, c.lastName].filter(Boolean).join(" ") || c.email || c.id;
      if (!term || named(label)) out.push({ entityType: "contact", id: c.id, label });
    }
    for (const a of accounts.data ?? []) {
      const label = a.legalName || a.tradingName || a.id;
      if (!term || named(label)) out.push({ entityType: "account", id: a.id, label });
    }
    for (const d of deals.data ?? []) {
      const label = d.dealName || d.id;
      if (!term || named(label)) out.push({ entityType: "deal", id: d.id, label });
    }
    for (const g of groups.data ?? []) {
      const label = g.groupName || g.id;
      if (!term || named(label)) out.push({ entityType: "client_group", id: g.id, label });
    }
    return out;
  }, [contacts.data, accounts.data, deals.data, groups.data, term]);

  const byType = (type: EntityType) => options.filter((option) => option.entityType === type).slice(0, 8);

  const groupsToRender: Array<{ type: EntityType; heading: string }> = [
    { type: "contact", heading: "Contacts" },
    { type: "account", heading: "Accounts" },
    { type: "deal", heading: "Deals" },
    { type: "client_group", heading: "Client groups" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0" data-testid="dialog-link-entity">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="text-base">Link selected emails to…</DialogTitle>
        </DialogHeader>
        <Command shouldFilter={false} className="rounded-none border-t">
          <CommandInput
            placeholder="Search contacts, accounts, deals, groups"
            value={search}
            onValueChange={setSearch}
            data-testid="link-entity-search"
          />
          <CommandList>
            {pending ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Linking…
              </div>
            ) : options.length === 0 ? (
              <CommandEmpty>No matching records.</CommandEmpty>
            ) : (
              groupsToRender.map(({ type, heading }) => {
                const rows = byType(type);
                if (rows.length === 0) return null;
                return (
                  <CommandGroup key={type} heading={heading}>
                    {rows.map((option) => (
                      <CommandItem
                        key={`${option.entityType}-${option.id}`}
                        value={`${option.entityType}-${option.id}`}
                        disabled={pending}
                        onSelect={() => onPick(option.entityType, option.id)}
                        data-testid={`link-option-${option.id}`}
                      >
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
